import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "@emotion/react/jsx-runtime";
import { useState, useEffect } from 'react';
import { css } from '@emotion/react';
import { space, from } from '@guardian/source-foundations';
import { CommentCount } from '@frontend/web/components/CommentCount';
import { RightColumn } from '@frontend/web/components/RightColumn';
import { AdSlot } from '@root/src/web/components/AdSlot';
import { App as Comments } from '@guardian/discussion-rendering';
import { Portal } from '@frontend/web/components/Portal';
import { Lazy } from '@frontend/web/components/Lazy';
import { Flex } from '@frontend/web/components/Flex';
import { SignedInAs } from '@frontend/web/components/SignedInAs';
import { ContainerLayout } from '@frontend/web/components/ContainerLayout';
import { Hide } from '@frontend/web/components/Hide';
import { getCommentContext } from '@root/src/web/lib/getCommentContext';
import { joinUrl } from '@root/src/lib/joinUrl';
import { useDiscussion } from '@root/src/web/lib/useDiscussion';
import { decidePalette } from '../lib/decidePalette';
const commentIdFromUrl = () => {
    if (typeof window === 'undefined')
        return;
    const { hash } = window.location;
    if (!hash)
        return;
    if (!hash.includes('comment'))
        return;
    if (!hash.split('-')[1])
        return;
    return parseInt(hash.split('-')[1], 10);
};
export const Discussion = ({ format, discussionApiUrl, shortUrlId, isCommentable, user, discussionD2Uid, discussionApiClientHeader, enableDiscussionSwitch, isAdFreeUser, shouldHideAds, beingHydrated, }) => {
    const [commentPage, setCommentPage] = useState();
    const [commentPageSize, setCommentPageSize] = useState();
    const [commentOrderBy, setCommentOrderBy] = useState();
    const [isExpanded, setIsExpanded] = useState(false);
    const [hashCommentId, setHashCommentId] = useState(commentIdFromUrl());
    const { commentCount, isClosedForComments } = useDiscussion(joinUrl([discussionApiUrl, 'discussion', shortUrlId]));
    const palette = decidePalette(format);
    const hasCommentsHash = typeof window !== 'undefined' &&
        window.location &&
        window.location.hash === '#comments';
    const handlePermalink = (commentId) => {
        if (typeof window === 'undefined')
            return false;
        window.location.hash = `#comment-${commentId}`;
        const comment = window.document.getElementById(`comment-${commentId}`);
        if (comment) {
            // The comment was already on the page so just scroll to it.
            comment.scrollIntoView();
        }
        setHashCommentId(commentId);
        return false;
    };
    const handleExpanded = (value) => {
        const { ga } = window;
        if (!ga) {
            return;
        }
        ga('allEditorialPropertyTracker.send', 'event', {
            eventCategory: 'Performance',
            eventAction: 'DiscussionExpanded',
            eventValue: Math.round(value),
            nonInteraction: true,
        });
    };
    // Check the url to see if there is a comment hash, e.g. ...crisis#comment-139113120
    // If so, make a call to get the context of this comment so we know what page it is
    // on.
    useEffect(() => {
        if (hashCommentId) {
            getCommentContext(discussionApiUrl, hashCommentId)
                .then((context) => {
                setCommentPage(context.page);
                setCommentPageSize(context.pageSize);
                setCommentOrderBy(context.orderBy);
                setIsExpanded(true);
            })
                .catch((e) => console.error(`getCommentContext - error: ${e}`));
        }
    }, [discussionApiUrl, hashCommentId]);
    useEffect(() => {
        if (hasCommentsHash) {
            setIsExpanded(true);
        }
    }, [hasCommentsHash]);
    const hideAd = isAdFreeUser || shouldHideAds;
    return (_jsxs(_Fragment, { children: [commentCount !== undefined && beingHydrated && (_jsx(Portal, Object.assign({ rootId: "comment-count-root" }, { children: _jsx(CommentCount, { isCommentable: isCommentable, commentCount: commentCount, palette: palette, setIsExpanded: setIsExpanded }, void 0) }), void 0)), _jsx(ContainerLayout, Object.assign({ padSides: false, padContent: false, 
                // If we're not hiding an advert stretch to the right
                stretchRight: !hideAd, leftContent: 
                // eslint-disable-next-line react/jsx-wrap-multilines
                _jsx(SignedInAs, { palette: palette, enableDiscussionSwitch: enableDiscussionSwitch, user: user, commentCount: commentCount, isClosedForComments: isClosedForComments }, void 0) }, { children: _jsxs(Flex, { children: [_jsxs("div", Object.assign({ css: css `
							${from.leftCol} {
								padding-left: 10px;
							}
							width: 100%;
							max-width: 100%;
						` }, { children: [_jsx(Hide, Object.assign({ when: "above", breakpoint: "leftCol" }, { children: _jsx("div", Object.assign({ css: css `
									padding-bottom: ${space[2]}px;
								` }, { children: _jsx(SignedInAs, { palette: palette, enableDiscussionSwitch: enableDiscussionSwitch, user: user, commentCount: commentCount, isClosedForComments: isClosedForComments }, void 0) }), void 0) }), void 0), beingHydrated && isExpanded && (_jsx(Comments, { user: user, baseUrl: discussionApiUrl, pillar: format.theme, initialPage: commentPage, pageSizeOverride: commentPageSize, isClosedForComments: isClosedForComments ||
                                        !enableDiscussionSwitch, orderByOverride: commentOrderBy, shortUrl: shortUrlId, additionalHeaders: {
                                        'D2-X-UID': discussionD2Uid,
                                        'GU-Client': discussionApiClientHeader,
                                    }, expanded: isExpanded, commentToScrollTo: hashCommentId, onPermalinkClick: handlePermalink, apiKey: "dotcom-rendering", onExpanded: (value) => {
                                        handleExpanded(value);
                                    } }, void 0)), beingHydrated && !isExpanded && (_jsx(Lazy, Object.assign({ margin: 300, disableFlexStyles: true }, { children: _jsx(Comments, { user: user, baseUrl: discussionApiUrl, pillar: format.theme, initialPage: commentPage, pageSizeOverride: commentPageSize, isClosedForComments: isClosedForComments ||
                                            !enableDiscussionSwitch, orderByOverride: commentOrderBy, shortUrl: shortUrlId, additionalHeaders: {
                                            'D2-X-UID': discussionD2Uid,
                                            'GU-Client': discussionApiClientHeader,
                                        }, expanded: isExpanded, commentToScrollTo: hashCommentId, onPermalinkClick: handlePermalink, apiKey: "dotcom-rendering", onExpanded: (value) => {
                                            handleExpanded(value);
                                        } }, void 0) }), void 0))] }), void 0), _jsx(_Fragment, { children: !hideAd && (_jsx(RightColumn, { children: _jsx("div", Object.assign({ css: css `
										position: static;
										height: 100%;
										padding-left: 20px;
									` }, { children: _jsx(AdSlot, { position: "comments", display: format.display }, void 0) }), void 0) }, void 0)) }, void 0)] }, void 0) }), void 0)] }, void 0));
};
